import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useHaptic } from '../../hooks/useHaptic';
import { STAKING_MOCK, useStakingActions } from '../../hooks/useStaking';
import { useWallet } from '../../hooks/useWallet';
import { StakeConfirmSheet } from './StakeConfirmSheet';
import { stakingErrorMessage, type Vault, type VaultKind } from '../../lib/staking';
import { VAULT_ACCENT, fmtUsd, fmtWhole, tint, vaultKey } from './stakeMeta';

type Props = {
  vaults: Vault[];
  /** Vault to select and scroll to — set when "Top up" is tapped on a card. */
  focusVault?: VaultKind | null;
  onFocusConsumed?: () => void;
  /** USDT TRC-20 address deposits are paid to; null = staking unavailable. */
  treasury: string | null;
  onDone: (message: string) => void;
};

const VAULT_GLYPH: Record<VaultKind, ReactElement> = {
  liquid: <path d="M12 3.6C12 3.6 6.2 9.8 6.2 14.2a5.8 5.8 0 0 0 11.6 0C17.8 9.8 12 3.6 12 3.6Z" />,
  monthly: (
    <>
      <path d="M4.5 15.5a7.5 7.5 0 0 1 15 0" />
      <path d="M12 15.5l3.4-4.1" />
      <circle cx="12" cy="15.5" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  maximum: (
    <>
      <path d="M4 16.5l4.8-4.8 3 3L19 7.5" />
      <path d="M14.8 7.5H19v4.2" />
    </>
  ),
};

const RULE_SUFFIXES = ['Rule1', 'Rule2', 'Rule3', 'Rule4'] as const;
const QUICK_ADDS = [50, 100, 500] as const;

const AMOUNT_GRADIENT =
  'linear-gradient(102deg,#d9e0ee 0%,#f2dcf0 16%,#ddf1e3 33%,#f8eed8 50%,#dde6f8 67%,#f3dcef 84%,#e2e9f4 100%)';

const CARD_SHADOW =
  '0 10px 28px rgba(0,0,0,.05),0 2px 6px rgba(0,0,0,.028),inset 0 1px 1.5px rgba(255,255,255,.9),inset 0 0 0 .5px rgba(255,255,255,.5)';

/** Amount form first, then vault segment + expandable details — matches the mockup. */
export function StakeVaultPanel({ vaults, treasury, focusVault, onFocusConsumed, onDone }: Props) {
  const { t } = useTranslation();
  const { impact, selection, notify } = useHaptic();
  const { open } = useStakingActions();
  const { byToken, byTokenChain } = useWallet();

  const [kind, setKind] = useState<VaultKind>('liquid');
  const [raw, setRaw] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Prefer TRC-20 USDT (staking network); fall back to total USDT across chains.
  const availableUsdt = byTokenChain['USDT:trx'] ?? byToken['USDT'] ?? 0;

  // "Top up" on a position card selects that vault here and brings the form
  // into view — the deposit form is the top-up form.
  useEffect(() => {
    if (!focusVault) return;
    setKind(focusVault);
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    onFocusConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusVault]);

  const vault = vaults.find((v) => v.kind === kind) ?? vaults[0];
  const accent = vault ? VAULT_ACCENT[vault.kind] : VAULT_ACCENT.liquid;
  const amount = Number(raw) || 0;

  // Daily compounding over the full term — the "in a year ≈ …" preview.
  const projected = useMemo(
    () => (vault ? amount * Math.pow(1 + vault.dailyRate, vault.termDays) : 0),
    [amount, vault],
  );

  if (!vault) return null;

  const belowMin = amount > 0 && amount < vault.minAmount;
  const canStake = amount >= vault.minAmount && !open.isPending && !vault.closed;

  const displayAmount = raw === '' ? '0' : raw;

  const setAmount = (n: number) => {
    const clamped = Math.max(0, Math.min(n, vault.maxAmount));
    setRaw(clamped > 0 ? String(Math.round(clamped * 100) / 100) : '');
  };

  const stake = async () => {
    if (!canStake) return;
    impact('medium');
    // A real deposit is an on-chain payment first, so it needs the confirm
    // sheet (source wallet, network fee, reauth). The fixture skips all that.
    if (!STAKING_MOCK) {
      setConfirming(true);
      return;
    }
    try {
      await open.mutateAsync({ vault, amount });
      notify('success');
      onDone(t('stake.staked', { amount: fmtWhole(amount), vault: t(vaultKey(kind, 'Name')) }));
      setRaw('');
    } catch (e) {
      notify('error');
      onDone(stakingErrorMessage(String(e), t, t('stake.errGeneric')));
    }
  };

  const exampleResult = 1000 * Math.pow(1 + vault.dailyRate, vault.termDays);

  return (
    <div ref={rootRef}>
      <div
        className="rounded-[22px] px-4 pb-3.5 pt-4"
        style={{
          background: 'linear-gradient(180deg,rgba(255,255,255,.93),rgba(255,255,255,.72))',
          backdropFilter: 'blur(22px) saturate(165%)',
          WebkitBackdropFilter: 'blur(22px) saturate(165%)',
          boxShadow: CARD_SHADOW,
        }}
      >
        {/* Selected vault name + APY */}
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-semibold text-[#6d6d80]">{t(vaultKey(kind, 'Name'))}</div>
          <div className="text-[12.5px] font-bold" style={{ color: accent }}>
            {t('stake.apy', { min: vault.apyMin, max: vault.apyMax })}
          </div>
        </div>

        {/* Holographic amount input */}
        <div
          className="relative mt-3 cursor-text rounded-[22px] px-3.5 pb-[13px] pt-3.5"
          style={{
            background: AMOUNT_GRADIENT,
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,.8), inset 0 0 0 .5px rgba(0,0,0,.05)',
          }}
          onClick={() => inputRef.current?.focus()}
        >
          <div className="relative text-center">
            <div
              className="pointer-events-none text-[56px] font-extrabold leading-[1.2] tracking-[-2px] tabular-nums text-[#0f1014]"
              aria-hidden="true"
            >
              ${displayAmount}
            </div>
            <input
              ref={inputRef}
              inputMode="decimal"
              value={raw}
              onChange={(e) => setRaw(e.target.value.replace(/[^\d.]/g, ''))}
              aria-label={t('stake.amount')}
              className="absolute inset-0 w-full bg-transparent text-center text-[56px] font-extrabold text-transparent outline-none caret-transparent"
            />
          </div>
          <div className="mt-1.5 text-center text-[11.5px] text-[rgba(30,35,50,.5)]">
            {t('stake.limitsCaption', {
              available: availableUsdt.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              min: fmtWhole(vault.minAmount),
              max: fmtWhole(vault.maxAmount),
            })}
          </div>
        </div>

        {/* Quick-add chips */}
        <div className="mt-3 flex justify-center gap-[7px]">
          {QUICK_ADDS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                selection();
                setAmount(amount + n);
              }}
              className="flex-1 rounded-[20px] bg-black/[0.06] py-[7px] text-[12px] font-semibold text-[#6d6d80]"
            >
              +${n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              selection();
              setAmount(
                availableUsdt > 0 ? Math.min(availableUsdt, vault.maxAmount) : vault.maxAmount,
              );
            }}
            className="flex-1 rounded-[20px] bg-black/[0.06] py-[7px] text-[12px] font-semibold text-[#6d6d80]"
          >
            {t('stake.max')}
          </button>
        </div>

        {vault.closed ? (
          <div
            className="mt-3 flex items-center gap-2.5 rounded-[14px] px-3.5 py-3"
            style={{ background: tint(accent, 0.09), color: accent }}
          >
            <LockGlyph size={16} />
            <span className="text-[12.5px] font-medium leading-[1.4]">{t('stake.closedNote')}</span>
          </div>
        ) : (
          <>
            {/* Year profit preview */}
            <div className="mt-3 rounded-[14px] bg-[rgba(52,199,89,0.09)] px-3.5 py-3 text-center text-[13.5px] font-medium leading-[1.45] text-ink">
              {amount > 0 && !belowMin ? (
                <>
                  {t('stake.yearPreview', { amount: fmtUsd(projected) })}
                  {' · '}
                  <span className="font-bold text-[#22b45e]">
                    {t('stake.profitPlus', { amount: fmtUsd(projected - amount) })}
                  </span>
                </>
              ) : belowMin ? (
                t('stake.minAmount', { amount: vault.minAmount })
              ) : (
                t('stake.yearPreview', { amount: fmtUsd(0) })
              )}
            </div>

            <button
              type="button"
              disabled={!canStake}
              onClick={() => void stake()}
              className="mt-3 h-[50px] w-full rounded-[15px] bg-[#1a1a1e] text-[15px] font-bold text-white transition-opacity disabled:opacity-40"
            >
              {open.isPending ? t('common.loading') : t('stake.stakeNow')}
            </button>
          </>
        )}

        {/* Vault segment picker */}
        <div className="mt-3.5 flex gap-1 rounded-2xl bg-black/[0.05] p-1">
          {vaults.map((v) => {
            const active = v.kind === kind;
            const c = VAULT_ACCENT[v.kind];
            return (
              <button
                key={v.kind}
                type="button"
                onClick={() => {
                  selection();
                  setKind(v.kind);
                }}
                className="flex flex-1 flex-col items-center gap-0.5 rounded-[13px] px-1 py-2 transition-[background,box-shadow,color] duration-150"
                style={{
                  color: active ? c : '#9d9da8',
                  background: active ? '#fff' : 'transparent',
                  transform: active ? 'scale(1.04)' : undefined,
                  opacity: v.closed && !active ? 0.6 : 1,
                  boxShadow: active
                    ? `0 6px 18px ${tint(c, 0.25)}, 0 1px 4px rgba(0,0,0,.12), inset 0 0 0 1.5px ${tint(c, 0.35)}`
                    : 'none',
                }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={active ? 2.3 : 1.9}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  {VAULT_GLYPH[v.kind]}
                </svg>
                <span
                  className={`inline-flex items-center gap-0.5 text-[11px] ${
                    active ? 'font-bold' : 'font-semibold'
                  }`}
                >
                  {v.closed && <LockGlyph size={10} />}
                  {t(vaultKey(v.kind, 'Short'))}
                </span>
              </button>
            );
          })}
        </div>

        {/* Expand / collapse vault details */}
        <button
          type="button"
          onClick={() => {
            selection();
            setDetailsOpen((o) => !o);
          }}
          className="mt-2.5 flex h-[34px] w-full items-center justify-center"
          aria-expanded={detailsOpen}
        >
          <svg
            width="26"
            height="26"
            fill="none"
            stroke="#b6b6c2"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            className="transition-transform duration-300"
            style={{ transform: detailsOpen ? 'rotate(180deg)' : undefined }}
          >
            <path d="M7 10l5 5 5-5" />
          </svg>
        </button>

        <div
          className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
          style={{
            maxHeight: detailsOpen ? 640 : 0,
            opacity: detailsOpen ? 1 : 0,
          }}
        >
          <div className="pb-0.5 pt-1">
            <div className="mb-3.5 flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px]"
                  style={{ background: tint(accent, 0.13), color: accent }}
                >
                  <svg
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    {VAULT_GLYPH[vault.kind]}
                  </svg>
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <div className="text-[16px] font-semibold tracking-[-0.3px] text-ink">
                      {t(vaultKey(kind, 'Name'))}
                    </div>
                    <span
                      className="max-w-full rounded-lg px-2 py-[3px] text-[10px] font-bold leading-[1.3]"
                      style={{ background: tint(accent, 0.11), color: accent }}
                    >
                      {t(vaultKey(kind, 'Badge'))}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted">{t(vaultKey(kind, 'Sub'))}</div>
                </div>
              </div>
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2">
              <DetailStat
                label={t('stake.statDaily')}
                value={`~${(vault.dailyRate * 100).toFixed(2)}%`}
                color={accent}
              />
              <DetailStat
                label={t('stake.statWithdraw')}
                value={t(vaultKey(kind, 'Principal'))}
              />
              <DetailStat
                label={t('stake.statApy')}
                value={t('stake.apyShort', { min: vault.apyMin, max: vault.apyMax })}
                color={accent}
              />
            </div>

            <div className="mb-2.5">
              {RULE_SUFFIXES.map((s) => (
                <div key={s} className="flex items-start gap-2 py-1 text-[12.5px] text-[#6d6d80]">
                  <svg
                    width="15"
                    height="15"
                    fill="none"
                    stroke={accent}
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                    className="mt-[1px] shrink-0"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  <span>{t(vaultKey(kind, s))}</span>
                </div>
              ))}
            </div>

            <div className="mb-3 rounded-xl bg-black/[0.035] px-3.5 py-2.5 text-[12.5px] font-semibold leading-[1.5] text-ink">
              {t('stake.example', {
                amount: fmtWhole(1000),
                result: fmtWhole(exampleResult),
              })}
              <span className="text-muted">
                {' · '}
                {t(vaultKey(kind, 'Payout'))}
              </span>
            </div>

            <p className="mb-4 text-[12px] leading-[1.6] text-muted">{t(vaultKey(kind, 'Desc'))}</p>
          </div>
        </div>
      </div>

      <StakeConfirmSheet
        open={confirming}
        amount={amount}
        vault={vault}
        treasury={treasury}
        onClose={() => setConfirming(false)}
        onDone={(message) => {
          setConfirming(false);
          setRaw('');
          onDone(message);
        }}
      />
    </div>
  );
}

/** Padlock shown on a vault that is closed for new deposits. */
function LockGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden="true"
    >
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </svg>
  );
}

function DetailStat({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col justify-center rounded-xl bg-black/[0.04] px-1.5 py-2.5 text-center">
      <div className="mb-1 text-[10px] font-bold uppercase leading-[1.2] tracking-[0.4px] text-muted">
        {label}
      </div>
      <div
        className="text-[12.5px] font-semibold leading-[1.25]"
        style={{ color: color ?? '#121212' }}
      >
        {value}
      </div>
    </div>
  );
}
